import { type FC } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { TaskStateBar } from '@/components/task-state-bar';
import { TaskState } from '@/types/uploads';

const isTerminal = (state: TaskState) => state === TaskState.Finished || state === TaskState.Errored;

/**
 * Export progress bars for share viewers (OTP guests and authenticated tenant
 * members — anonymous viewers can't create a share export at all), rendered in
 * the SideNavigation on public share pages — the share-page counterpart to the
 * authenticated ExportTaskBars.
 *
 * Renders ALL states (not just in-progress): like the authed ExportTaskBar, a
 * Finished bar shows its completion (✓) state and self-dismisses after
 * TaskStateBar's onFinished dwell, while errored bars linger until dismissed.
 * Removal is owned here (onFinished/onCancel → removeExport) so a viewer
 * actually sees the export finish instead of the bar blinking out the instant
 * it completes.
 */
export const ShareExportProgressBars: FC<{ collapsed?: boolean }> = ({ collapsed = false }) => {
  const exports = useExportStore(useShallow((state) => state.exports));
  const removeExport = useExportStore((state) => state.removeExport);

  if (exports.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-px" data-cy="share-export-progress-bars">
      {exports.map((task) => (
        <TaskStateBar
          key={task.id}
          open={false}
          taskId={task.id}
          state={task.state}
          progress={task.progress}
          title={task.label}
          collapsed={collapsed}
          onFinished={() => removeExport(task.id)}
          {...(!collapsed && { onCancel: isTerminal(task.state) ? () => removeExport(task.id) : undefined })}
        />
      ))}
    </div>
  );
};
